import { useNavigate } from "react-router-dom"
import useTargetStorage from "@/hooks/useTargetStorage"
import { useTranslation } from "react-i18next"
import useStorages from "hooks/useStorages"
import useCategories from "hooks/useCategories"
import useStorageLimits from "hooks/useStorageLimits"
import Section from "components/layout/section"
import SectionHeader from "components/layout/sectionHeader"
import SectionHeaderTitle from "components/layout/sectionHeaderTitle"
import RightSidebar from "components/sidebar/right/RightSidebar"
import SmallDataBox from "components/dataBox/smallDataBox"
import DataBoxHolder from "components/dataBox/dataBoxHolder"
import PageTitle from "features/navigation/pageTitle"
import { FormStatus } from "@/types"
import { paramGuardStorage } from "@/utils/paramGuard"

const StorageStatisticsPage = () => {

    const { t } = useTranslation()
    const navigate = useNavigate()

    const { targetStorageDocId: storageId } = useTargetStorage()

    const {
        storage,
        storageStatus
    } = useStorages({storageId})

    const {
        allCategories
    } = useCategories({storageId})

    // Get storage plan limits
    const storagePlan = storage?.settings?.storagePlan
    const { limits } = useStorageLimits({ 
        storagePlan,
        currentProductCount: 0,
        currentCategoryCount: 0
    })

    // Check params and declare safe ones to use in JSX
    if (!paramGuardStorage({storageId, storage, storageStatus, navigate})) {
        return <></>
    }
    const safeStorageId = storageId!;

    // Calculate statistics
    const totalProducts = storage?.productsStockStatus ? 
        (storage.productsStockStatus.highStock || 0) +
        (storage.productsStockStatus.lowStock || 0) +
        (storage.productsStockStatus.noStock || 0) +
        (storage.productsStockStatus.negativeStock || 0) : 0

    const totalCategories = allCategories?.filter(cat => !cat.isDeleted).length || 0

    // Render statistics page
    return (
        <>
            <PageTitle title={t('titleStatistics')} />
            <Section>
                {storageStatus === FormStatus.SUCCESS ?
                    <p>{t('statisticsDescription')}</p> :
                    <p className="skeleton"></p>
                }
            </Section>
            <Section>
                <SectionHeader>
                    <SectionHeaderTitle>
                        <h2>{t('titleInventoryOverview')}</h2>
                    </SectionHeaderTitle>
                </SectionHeader>
                <DataBoxHolder>
                    <SmallDataBox
                        label={t('labelTotalProducts')}
                        value={`${totalProducts} / ${limits.products}`}
                        colorScheme="blue"
                        isLoading={storageStatus !== FormStatus.SUCCESS}
                    />
                    <SmallDataBox
                        label={t('labelTotalCategories')}
                        value={`${totalCategories} / ${limits.categories}`}
                        colorScheme="blue"
                        isLoading={storageStatus !== FormStatus.SUCCESS}
                    />
                </DataBoxHolder>
            </Section>
            <Section>
                <SectionHeader>
                    <SectionHeaderTitle>
                        <h2>{t('titleStockStatus')}</h2>
                    </SectionHeaderTitle>
                </SectionHeader>
                <DataBoxHolder>
                    <SmallDataBox
                        label={t('labelHighStock')}
                        value={storage?.productsStockStatus?.highStock || 0}
                        colorScheme="green"
                        isLoading={storageStatus !== FormStatus.SUCCESS}
                    />
                    <SmallDataBox
                        label={t('labelLowStock')}
                        value={storage?.productsStockStatus?.lowStock || 0}
                        colorScheme="yellow"
                        isLoading={storageStatus !== FormStatus.SUCCESS}
                    />
                    <SmallDataBox
                        label={t('labelNoStock')}
                        value={storage?.productsStockStatus?.noStock || 0}
                        colorScheme="red"
                        isLoading={storageStatus !== FormStatus.SUCCESS}
                    />
                    <SmallDataBox
                        label={t('labelNegativeStock')}
                        value={storage?.productsStockStatus?.negativeStock || 0}
                        colorScheme="red"
                        isLoading={storageStatus !== FormStatus.SUCCESS}
                    />
                </DataBoxHolder>
            </Section>
            <RightSidebar />
        </>
    )
}

export default StorageStatisticsPage
